import { useEffect, useId, useRef, useState } from 'react';
import { Button } from './Button';
import { TextField } from './TextField';
import { validateOrgName } from '../../lib/vm/useCreateOrgVM';

export interface OrgCreateFormProps {
  onSubmit: (name: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  onCancel: () => void;
  disabled?: boolean;
  variant: 'flyout' | 'settings';
  initialName?: string;
  testIdPrefix?: string;
}

/**
 * Org-creation form primitive.
 *
 * - Validates via the shared `validateOrgName` from useCreateOrgVM — no logic
 *   duplication.
 * - Composes `<TextField>` + `<Button>` primitives — no raw elements.
 * - Enter submits; Esc cancels.
 * - Create button disabled while name is empty/whitespace or while submitting.
 * - Error messages rendered via `<TextField errorText>`.
 *
 * CSS namespace: `pn-org-create-form` in primitives.css.
 */
export function OrgCreateForm({
  onSubmit,
  onCancel,
  disabled = false,
  variant,
  initialName = '',
  testIdPrefix = 'org-create-form'
}: OrgCreateFormProps) {
  const inputId = useId();
  const [name, setName] = useState(initialName);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The flyout variant unmounts this form on success while onSubmit is still
  // awaiting — guard the post-await state writes against that race.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const trimmed = name.trim();
  const isEmpty = trimmed.length === 0;
  // Button is visually disabled when empty or blocked, but the Enter handler
  // still validates even for empty so it can show the "required" error.
  const isBlocked = submitting || disabled;
  const isButtonDisabled = isEmpty || isBlocked;

  async function handleSubmit() {
    if (isBlocked) return;

    // Client-side validation first (shared validator) — runs even for empty names
    // so Enter on an empty field shows the error rather than being a silent no-op.
    const clientError = validateOrgName(name);
    if (clientError) {
      setErrorMessage(clientError.message);
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    const result = await onSubmit(trimmed);
    if (!mountedRef.current) return;
    setSubmitting(false);

    if (!result.ok) {
      setErrorMessage(result.error);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  }

  return (
    <div
      className="pn-org-create-form"
      data-variant={variant}
    >
      <TextField
        ref={inputRef}
        id={inputId}
        label="Org name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setErrorMessage(null);
        }}
        onKeyDown={handleKeyDown}
        errorText={errorMessage ?? undefined}
        disabled={submitting || disabled}
        autoFocus
        data-testid={`${testIdPrefix}-input`}
      />
      <div className="pn-org-create-form-actions">
        <Button
          variant="default"
          size="sm"
          onClick={onCancel}
          disabled={submitting || disabled}
          data-testid={`${testIdPrefix}-cancel`}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => { void handleSubmit(); }}
          disabled={isButtonDisabled}
          data-testid={`${testIdPrefix}-submit`}
        >
          {submitting ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </div>
  );
}
